import { ClientLoaderFunctionArgs, useLoaderData } from 'react-router';
import {
  StreamCategory,
  StreamPricingType,
  StreamState,
  StreamType,
} from '@/__generated__/graphql.ts';
import { gql } from '@/__generated__/index.ts';
import { preloadQuery } from '@/apollo/apollo-client.ts';
import { hasGridFeedPosition } from '@/components/feed/restore-feed/use-restore-grid-feed.ts';
import { paths } from '@/routes-utils/paths.ts';
import { protectLoader } from '@/routes-utils/protected-loader.ts';

export const StreamsAllQuery = gql(`
query StreamsAllQuery($limit: Int!, $nextToken: String, $streamCategory: StreamCategory, $custom: Boolean, $pricingType: StreamPricingType, $streamState: StreamState) {
  streamsAll(limit: $limit, nextToken: $nextToken, streamCategory: $streamCategory, custom: $custom, pricingType: $pricingType, streamState: $streamState) {
    items {
      ...StreamCardFragment
    }
    nextToken
  }
}
`);

const StreamsCreatedQuery = gql(`
query StreamsCreatedQuery($limit: Int!, $nextToken: String, $streamCategory: StreamCategory, $custom: Boolean, $pricingType: StreamPricingType, $streamState: StreamState, $streamType: StreamType) {
  streamsCreated(limit: $limit, nextToken: $nextToken, streamCategory: $streamCategory, custom: $custom, pricingType: $pricingType, streamState: $streamState, streamType: $streamType) {
    items {
      ...StreamCardFragment
    }
    nextToken
  }
}
`);

const StreamsFollowingQuery = gql(`
query StreamsFollowingQuery($limit: Int!, $nextToken: String, $streamCategory: StreamCategory, $custom: Boolean, $pricingType: StreamPricingType, $streamState: StreamState, $streamType: StreamType) {
  streamsFollowing(limit: $limit, nextToken: $nextToken, streamCategory: $streamCategory, custom: $custom, pricingType: $pricingType, streamState: $streamState, streamType: $streamType) {
    items {
      ...StreamCardFragment
    }
    nextToken
  }
}
`);

export const StreamsSearchQuery = gql(`
query StreamSearchQuery($query: String!, $offset: Int!, $streamCategory: StreamCategory, $custom: Boolean) {
  search(input: {limit: 20, offset: $offset, text: $query, filters: {type: [Stream], stream: {streamCategory: $streamCategory, custom: $custom}}}) {
    items {
      ...StreamCardFragment
    }
    offset
  }
}
`);

const getValueFromObject = <T extends Record<string, string>>(
  value: string | null,
  object: T,
  allowedValues?: T[keyof T][],
): T[keyof T] | null => {
  if (!value) return null;

  const values = Object.values(object) as T[keyof T][];
  if (!values.includes(value as T[keyof T])) return null;

  const matchedValue = value as T[keyof T];

  if (allowedValues && !allowedValues.includes(matchedValue)) {
    return null;
  }

  return matchedValue;
};

const getValueFromParams = (
  request: Request,
  paramName: string,
  defaultValue?: string,
): string | null => {
  const searchParams = new URL(request.url).searchParams;
  const paramValue = searchParams.get(paramName);

  if (!paramValue && defaultValue) return defaultValue;

  return paramValue;
};

const getParams = (request: Request) => {
  const url = new URL(request.url);
  const isCreatedStreams =
    url.pathname === paths.streamsCreated ||
    url.pathname === paths.customStreamsCreated;

  const categoryParam = getValueFromParams(request, 'category');
  const category = categoryParam
    ? getValueFromObject(categoryParam, StreamCategory)
    : null;

  const pricingTypeParam = getValueFromParams(
    request,
    'pricingType',
    StreamPricingType.All,
  );
  const pricingType = getValueFromObject(pricingTypeParam, StreamPricingType);

  const streamStateParam = getValueFromParams(request, 'streamState');
  const allowedStreamStates = !isCreatedStreams
    ? [StreamState.Active, StreamState.Inactive, StreamState.OnHold]
    : undefined;
  const streamState = getValueFromObject(
    streamStateParam,
    StreamState,
    allowedStreamStates,
  );

  const streamTypeParam = getValueFromParams(request, 'streamType');
  const streamType = getValueFromObject(streamTypeParam, StreamType);

  const query = getValueFromParams(request, 'q');

  return { category, pricingType, streamState, streamType, query };
};

const getIsCustomStreamsPage = (request: Request) => {
  const url = new URL(request.url);
  return url.pathname.startsWith(paths.customStreams);
};

export const streamsAllLoader = async ({
  request,
}: ClientLoaderFunctionArgs) => {
  const { pricingType, query, category, streamState } = getParams(request);

  const isCustomStreamsPage = getIsCustomStreamsPage(request);

  return {
    queryRef: !query
      ? preloadQuery(StreamsAllQuery, {
          variables: {
            limit: 20,
            nextToken: null,
            streamCategory: category,
            custom: isCustomStreamsPage,
            pricingType,
            streamState,
          },
          fetchPolicy: hasGridFeedPosition(history.state?.key)
            ? 'cache-first'
            : 'cache-and-network',
        })
      : null,
    searchQueryRef: query
      ? preloadQuery(StreamsSearchQuery, {
          variables: {
            query,
            offset: 0,
            streamCategory: category,
            custom: isCustomStreamsPage,
          },
          fetchPolicy: hasGridFeedPosition(history.state?.key)
            ? 'cache-first'
            : 'cache-and-network',
        })
      : null,
  };
};
streamsAllLoader.hydrate = true as const;

export const streamsCreatedLoader = protectLoader(async ({ request }) => {
  const { pricingType, query, category, streamState, streamType } =
    getParams(request);

  const isCustomStreamsPage = getIsCustomStreamsPage(request);

  return {
    queryRef: !query
      ? preloadQuery(StreamsCreatedQuery, {
          variables: {
            limit: 20,
            nextToken: null,
            streamCategory: category,
            custom: isCustomStreamsPage,
            pricingType,
            streamState,
            streamType,
          },
          fetchPolicy: hasGridFeedPosition(history.state?.key)
            ? 'cache-first'
            : 'cache-and-network',
        })
      : null,
    searchQueryRef: query
      ? preloadQuery(StreamsSearchQuery, {
          variables: {
            query,
            offset: 0,
            streamCategory: category,
            custom: isCustomStreamsPage,
          },
          fetchPolicy: hasGridFeedPosition(history.state?.key)
            ? 'cache-first'
            : 'cache-and-network',
        })
      : null,
  };
});

export const streamsFollowingLoader = protectLoader(async ({ request }) => {
  const { pricingType, query, category, streamState, streamType } =
    getParams(request);

  const isCustomStreamsPage = getIsCustomStreamsPage(request);

  return {
    queryRef: !query
      ? preloadQuery(StreamsFollowingQuery, {
          variables: {
            limit: 20,
            nextToken: null,
            streamCategory: category,
            custom: isCustomStreamsPage,
            pricingType,
            streamState,
            streamType,
          },
          fetchPolicy: hasGridFeedPosition(history.state?.key)
            ? 'cache-first'
            : 'cache-and-network',
        })
      : null,
    searchQueryRef: query
      ? preloadQuery(StreamsSearchQuery, {
          variables: {
            query,
            offset: 0,
            streamCategory: category,
            custom: isCustomStreamsPage,
          },
          fetchPolicy: hasGridFeedPosition(history.state?.key)
            ? 'cache-first'
            : 'cache-and-network',
        })
      : null,
  };
});

export type LoaderData = Awaited<ReturnType<typeof streamsAllLoader>>;

export const useStreamsLoaderData = () => useLoaderData() as LoaderData;
